<template>
  <t-watermark
    class="h-full"
    :alpha="0.3"
    :watermark-content="{
      text: waterMarkContentStore.watermarkContent,
    }"
    :width="120"
    :height="60"
    :y="120"
    :x="80"
    :z-index="watermarkZIndex"
  >
    <t-layout class="overflow-hidden h-full flex isolate">
      <!-- 根据isShowMenu参数控制侧边栏显示 -->
      <Sidebar
        v-if="isShowMenu"
        ref="sidebarRef"
        @on-show="showEvent"
      />
      <div
        class="flex-1 flex"
        :style="{ width: isShowMenu ? 'calc(100vw - 70px)' : '100vw' }"
      >
        <second-level-side-bar
          v-if="isShowMenu"
          ref="secondSideBarRef"
          :is-hover="isHover"
        />
        <t-content
          ref="tContentRef"
          class="h-[100vh] content-width overflow-y-auto relative bg-background isolate flex-1"
          @mouseenter="onMouseenter"
        >
          <FullLoading v-if="routerStore.isPageLoading" />
          <NetworkError v-if="routerStore.isPageLoadErr" />
          <router-view
            v-else
            :key="route.meta.name"
            v-slot="{ Component }"
          >
            <template v-if="Component && routerStore.isNowRouterAllow">
              <KeepAlive>
                <Suspense>
                  <component :is="Component" />
                  <template #fallback>
                    <FullLoading />
                  </template>
                </Suspense>
              </KeepAlive>
            </template>
            <NeedSelectGame v-else-if="routerStore.isRouterNeedSelectGame" />
            <PermissionDeny
              v-else
              :loading="!globalGame.isGameCodeDone || routerStore.isNowRouterAllow"
              class="content"
            />
          </router-view>
        </t-content>
      </div>
    </t-layout>
  </t-watermark>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import Sidebar from '@/components/Layout/Sidebar.vue';
import PermissionDeny from '@/components/Nullable/PermissionDeny.vue';
import NeedSelectGame from '@/components/Nullable/NeedSelectGame.vue';
import SecondLevelSideBar from '@/components/Layout/SecondLevelSideBar.vue';
import { useRouterStore } from '@/store/global/router.store';
import NetworkError from '@/components/Nullable/NetworkError.vue';
import { useGlobalGameStore } from '@/store/global/game.store';
import { useRoute } from 'vue-router';
import { getQueryString } from '@/utils/url';
import { useWaterMarkContentStore } from '@/store/global/watermark.store';
import type { Content } from 'tdesign-vue-next';

const route = useRoute();

const tContentRef = ref<InstanceType<typeof Content> | null>(null);
const secondSideBarRef = ref(null);
const sidebarRef = ref(null);

const routerStore = useRouterStore();
const globalGame = useGlobalGameStore();
const waterMarkContentStore = useWaterMarkContentStore();
// 左侧导航栏的展开收起状态-hover，shixiong取的一个啥鬼名字
const isHover = ref(false);

// 添加isShowMenu参数处理逻辑
const isShowMenu = computed(() => {
  const showMenuParam = getQueryString('isShowMenu');
  // 如果没有传递参数或参数为true，则显示菜单（默认显示）
  if (showMenuParam === null || showMenuParam === undefined || showMenuParam === '') {
    return true;
  }
  // 只有明确传递false时才隐藏菜单
  return showMenuParam !== 'false';
});

const contentBoxWidth = computed(() => {
  // 如果不显示菜单，内容区域占满全屏
  if (!isShowMenu.value) {
    return '100vw';
  }

  let baseWidth = 0;
  if ((secondSideBarRef?.value as any)?.showSecondSideBar) {
    // 二级目录的基础宽度是260px
    baseWidth += 260;
  }
  // 一级目录的基础展开时的宽度为200，收起时为70 px
  baseWidth += (sidebarRef?.value as any)?.collapsed ? 70 : 236;
  return `calc(100vw - ${baseWidth}px)`;
});

const isOnlyViewMode = ref<boolean>(getQueryString('view_mode') === 'only_view');

// 定义需要降低水印z-index的路由（Twitch视频播放相关）
const twitchRoutes = [
  'my_real_time_dashboard',
  'real-time'
];

// 计算水印的z-index值
const watermarkZIndex = computed(() => {
  if (isOnlyViewMode.value) {
    return undefined; // view_mode=only_view时不显示水印
  }

  // 检查当前路由是否是Twitch视频播放相关路由
  const currentPath = route.path;
  const isTwitchRoute = twitchRoutes.some(twitchRoute =>
    currentPath.includes(twitchRoute)
  );

  console.log('水印z-index控制 - 当前路由:', currentPath, '是否Twitch路由:', isTwitchRoute, '水印z-index:', isTwitchRoute ? -1 : 1);

  return isTwitchRoute ? -1 : 1; // Twitch路由使用-1，其他路由使用1
});

const onMouseenter = () => {
  if (routerStore.collapsed) {
    isHover.value = false;
    routerStore.isShowSecondMenu = true;
  }
};

watch(
  () => route?.path,
  () => {
    if (tContentRef.value?.$el) {
      tContentRef.value.$el.scrollTop = 0;
    }
  },
  { deep: true },
);

const showEvent = (val: boolean) => {
  isHover.value = val;
};
</script>

<style>
#app {
  /* @apply bg-white-primary; */
  @apply bg-background;
}

.content {
  @apply relative z-10 h-full bg-white-primary overflow-auto flex-1 w-full;
}
.content-width {
  width: v-bind(contentBoxWidth);
}
.loading-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  gap: 8px;
}

.loading-placeholder::before {
  content: '';
  width: 24px;
  height: 24px;
  border: 2px solid #E6E9F4;
  border-top: 2px solid #C67BE7;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

.loading-placeholder::after {
  content: 'Loading...';
  color: #747D98;
  font-size: 12px;
  font-weight: 400;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
</style>
